import React, { useEffect, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { ToastProvider } from "./context/ToastContext";
import { GoldPriceProvider } from "./context/GoldPriceContext";
import Navbar from "./components/Navbar";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/LoadingSpinner";
import useScrollToTop from "./utils/useScrollToTop";
import "./App.css";

// Lazy load pages for smaller initial bundle and faster first load
const Home = lazy(() => import("./components/Home"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Cart = lazy(() => import("./pages/Cart"));
const Orders = lazy(() => import("./pages/Orders"));
const Admin = lazy(() => import("./pages/Admin"));
const About = lazy(() => import("./pages/About"));
const GoldByWeight = lazy(() => import("./pages/GoldByWeight"));
const Certificates = lazy(() => import("./pages/Certificates"));
const CertificateVerification = lazy(() => import("./pages/CertificateVerification"));
const Shipping = lazy(() => import("./pages/Shipping"));
const Contact = lazy(() => import("./pages/Contact"));

function AppContent() {
  const { loading } = useAuth();
  const location = useLocation();
  useScrollToTop(loading);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      {loading && <LoadingSpinner message="Loading..." />}
      <Navbar />
      <main className="main-content">
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner message="Loading..." />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/about" element={<About />} />
              <Route path="/gold-by-weight" element={<GoldByWeight />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route
                path="/certificate-verification"
                element={<CertificateVerification />}
              />
              <Route path="/shipping" element={<Shipping />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <GoldPriceProvider>
        <CartProvider>
          <ToastProvider>
            <Router>
              <div className="App">
                <AppContent />
              </div>
            </Router>
          </ToastProvider>
        </CartProvider>
      </GoldPriceProvider>
    </AuthProvider>
  );
}

export default App;
